import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { loadMyGroups, loadGroupBySlug } from "@/lib/groups";
import { loadRanking } from "@/lib/ranking";
import { mediaUrl } from "@/lib/media";

type GroupRank = {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  position: number;
  total: number;
  points: number;
};

const STORAGE_KEY = (uid: string) => `myGroupRanks:${uid}`;

export function MyGroupRankings({ userId }: { userId: string }) {
  const [rows, setRows] = useState<GroupRank[]>([]);
  const [prev, setPrev] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY(userId));
        if (stored) setPrev(JSON.parse(stored));
      } catch {/* ignore */}

      const myGroups = await loadMyGroups(userId);
      if (myGroups.length === 0) {
        if (mounted) { setRows([]); setLoading(false); }
        return;
      }
      // Members per group via the dedicated /api/groups/by-slug/:slug endpoint
      // (it includes the embedded member list). One round-trip per group is
      // fine — users are typically in 1-3 groups.
      const detailedGroups = await Promise.all(
        myGroups.map(async (g) => {
          const detail = await loadGroupBySlug(g.slug).catch(() => null);
          return { ...g, members: detail?.members ?? [] };
        }),
      );
      const allUserIds = Array.from(
        new Set(detailedGroups.flatMap((g) => g.members.map((m: any) => m.user_id))),
      );
      const ranking = await loadRanking(allUserIds);

      const result: GroupRank[] = detailedGroups.map((g) => {
        const memberIds = new Set(g.members.map((m: any) => m.user_id));
        const subRanking = ranking
          .filter(r => memberIds.has(r.user_id))
          .sort((a, b) => b.points - a.points || b.exact - a.exact);
        const idx = subRanking.findIndex(r => r.user_id === userId);
        return {
          id: g.id,
          name: g.name,
          slug: g.slug,
          logo_url: g.logo_url,
          position: idx >= 0 ? idx + 1 : subRanking.length,
          total: memberIds.size,
          points: idx >= 0 ? subRanking[idx].points : 0,
        };
      }).sort((a, b) => a.position - b.position);

      if (!mounted) return;
      setRows(result);
      setLoading(false);

      // Snapshot speichern für nächsten Vergleich
      const snap: Record<string, number> = {};
      result.forEach(r => { snap[r.id] = r.position; });
      try { localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(snap)); } catch {/* ignore */}
    })();
    return () => { mounted = false; };
  }, [userId]);

  if (loading) {
    return (
      <div className="space-y-1.5 pt-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1">
          👥 Deine Gruppen-Platzierungen
        </div>
        <div className="bg-muted/40 rounded-md h-12 animate-pulse" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <Link
        to="/gruppen"
        className="block bg-secondary/40 border border-dashed border-border rounded-md p-2.5 text-center text-xs text-muted-foreground hover:bg-secondary/60 tap"
      >
        👥 Tritt einer Tippgruppe bei und mess dich mit Freunden →
      </Link>
    );
  }

  return (
    <div className="space-y-1.5 pt-1">
      <div className="flex items-center justify-between px-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          👥 Deine Gruppen-Platzierungen
        </div>
        <Link to="/gruppen" className="text-[10px] font-bold text-accent hover:underline">
          Alle ›
        </Link>
      </div>
      <LayoutGroup>
        <div className="space-y-1.5">
          <AnimatePresence initial={false}>
            {rows.map((r) => {
              const prevPos = prev[r.id];
              const delta = prevPos != null ? prevPos - r.position : 0; // positive = aufgestiegen
              const isLeader = r.position === 1;
              const highlightBg =
                delta > 0 ? "hsl(var(--accent) / 0.35)"
                : delta < 0 ? "hsl(var(--destructive) / 0.25)"
                : "transparent";
              return (
                <motion.div
                  key={r.id}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    backgroundColor: delta !== 0
                      ? [highlightBg, "hsl(var(--card))"]
                      : "hsl(var(--card))",
                  }}
                  exit={{ opacity: 0 }}
                  transition={{
                    layout: { type: "spring", stiffness: 320, damping: 28 },
                    backgroundColor: { duration: 1.4, ease: "easeOut" },
                  }}
                >
                  <Link
                    to={`/gruppe/${r.slug}`}
                    className="flex items-center gap-2.5 rounded-md px-2.5 py-2 border border-border tap"
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center overflow-hidden shrink-0 text-base relative">
                      <span>👥</span>
                      {r.logo_url && (
                        <img
                          src={mediaUrl(r.logo_url)}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate leading-tight">{r.name}</div>
                      <div className="text-[10px] text-muted-foreground tabular-nums">
                        {r.points} Punkte
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-extrabold text-base tabular-nums leading-none">
                        #{r.position}
                        <span className="text-[10px] text-muted-foreground font-semibold">
                          {" "}/ {r.total}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] font-bold leading-none">
                        {isLeader && (
                          <span className="bg-gold rounded px-1 py-0.5 font-extrabold">
                            ★ Leader
                          </span>
                        )}
                        {!isLeader && delta > 0 && (
                          <span className="text-accent">▲ +{delta}</span>
                        )}
                        {!isLeader && delta < 0 && (
                          <span className="text-destructive">▼ {delta}</span>
                        )}
                        {!isLeader && delta === 0 && prevPos != null && (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </LayoutGroup>
    </div>
  );
}
